/** @jsxImportSource @emotion/react */
import * as React from 'react';
import profileStub from '../../assets/images/profilePlaceholder.jpg';
import { css } from '@emotion/react';
import { GuestBasics } from '../../guest/guestBasics';
import AddressView from '../../guest/guestContact/AddressView';
import { BLACK, DARK_GREY } from '../../styles/colors';
import { calcAge, formatDate } from '../../language/time';
import { prettyPhone } from '../../guest/guestContact/module';
import { ellipsizeAtSecondLine, CIRCULAR_RADIUS } from '../../guest/guestCard/guestCardStyles';
import GroupMenu from './GroupMenu';
import { COLUMN_SPACING, ROW_SPACING } from '../../forms/styles';
import { onIe } from '../../language/browser';
import { LargeText, SmallText, UNSELECTABLE_TEXT } from '../../styles/styles';
import { getResource } from '../../language/resources/getResource';


const WRAP_TO_COLUMN = 960;

const flexColumn = css`
  display: flex;
  flex-direction: column;
`;

const columnSpacing = css`
    & > span {
      display: block;
    }
    
    & > * {
      margin-bottom: 5px;
    }
    
    & > *:last-child {
      margin-bottom: 0;
    }
  `;

const styles = {
  root:         css`
    margin: 22px;
    display: flex;
    flex-wrap: wrap;
    flex-grow:  1;
    
    /* Row at larger screen sizes */
    @media (min-width: ${WRAP_TO_COLUMN}px) {
      flex-direction: row;
      ${ROW_SPACING};
    }
    
    /* Column at smaller screen sizes */
    @media (max-width: ${WRAP_TO_COLUMN - 1}px) {
      flex-direction: column;
      ${COLUMN_SPACING};
    }
  `,
  firstName:    css`
    ${LargeText}; 
    font-weight: bold;
    color: ${BLACK};
    
    ${ellipsizeAtSecondLine};
  `,
  lastName:     css`
    ${LargeText}; 
    font-weight: bold;
    color: ${DARK_GREY};
    
    ${ellipsizeAtSecondLine};
  `,
  detailLine:   css`
    ${SmallText}; 
    color: ${BLACK};
  `,
  detailColumn: css`
    ${columnSpacing};
    max-width: 100%; 
    width: 100%;
    /* Row try to fill parent width */
    flex: 1 1;

    /* Only on desktop/large displays to center text */
    @media (min-width: ${WRAP_TO_COLUMN - 1}px) {
      margin-top: 25px;
      margin-bottom: 25px;
    }
    
    /* Column align stretch */
    @media (max-width: ${WRAP_TO_COLUMN - 1}px) {
      flex-shrink: 0;
      flex-grow: 1;
      
      ${
    // IE does this weird 0 height thing when this goes to column??
    onIe() ? css`flex-basis: 80px;` : ''
    }
    }
  `,
};

const horizontalGuestDetailCss = css`
  ${flexColumn};
  align-items: center;
  justify-content: center;
  height: 100px;
  width: 100px;
  border: 2px solid;
  position: relative;
  transition: border-color 500ms ease-in;
  overflow: hidden;
  border: 3px solid;
  border-radius: ${CIRCULAR_RADIUS};
  border-color: transparent;
`;

const guestPhoto = css`
  width: 100%;
  height: 100%;
  ${UNSELECTABLE_TEXT};
`;

const groupMenu = css`
  /* When wrapping as a column, don't fill width */
  @media (max-width: ${WRAP_TO_COLUMN - 1}px) {
    align-self: flex-start;
  }
`;


/**
 * HorizontalGuestDetails: a component that shows a guest's basic details. Seen as the guest details above the expanding
 * part for each guest search result. This lays out the guest's information top-to-bottom, left-to-right with
 * placeholder text in cases where the guest e.g. doesn't have an address or primary phone number.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class HorizontalGuestDetails extends React.PureComponent<{
  resources: Record<string, any>;
  dateFormat: string;
  guest: GuestBasics;
  householdSize: number;
  isExpanded: boolean;
  onClick?: () => void;
  /**
   * The guest's photo as currently loaded in the guest card (see getPhotoData). Takes precedence over
   * guest.smallPhotoData, which is only captured when the search runs and so goes stale after a photo upload.
   */
  photoData?: string;
}> {
  public render = () => (
    <div css={styles.root}>
      {/* Photo */}
      <div css={horizontalGuestDetailCss}>
        <img title={`${this.props.guest.firstName} ${this.props.guest.lastName}`}
             src={this.props.photoData || this.props.guest.smallPhotoData || profileStub}
             alt='Guest Photo'
             css={guestPhoto}/>
      </div>

      {/* First / Last / Age */}
      <div css={css`
             ${flexColumn};
             ${styles.detailColumn};
           `}>
        <span title={getResource(this.props.resources, 'HORIZONTAL_GUEST_DETAIL_FIRST_NAME')} css={styles.firstName} id={`first-name-${this.props.guest.masterGuestId}`}>
          {this.props.guest.firstName || getResource(this.props.resources, 'HORIZONTAL_GUEST_DETAIL_NO_FIRST_NAME')}
        </span>
        <span title={getResource(this.props.resources, 'HORIZONTAL_GUEST_DETAIL_LAST_NAME')} css={styles.lastName} id={`last-name-${this.props.guest.masterGuestId}`}>
          {this.props.guest.lastName || getResource(this.props.resources, 'HORIZONTAL_GUEST_DETAIL_NO_LAST_NAME')}
        </span>
        <span
          title={formatDate(this.props.guest.dob, this.props.dateFormat)}
          css={styles.detailLine}
          id={`dob-${this.props.guest.masterGuestId}`}
        >
          {getResource(this.props.resources, 'HORIZONTAL_GUEST_DETAIL_YEARS_OLD', calcAge(this.props.guest.dob)) || getResource(this.props.resources, 'HORIZONTAL_GUEST_DETAIL_NO_BIRTH_DATE')}
        </span>
      </div>

      {/* Address */}
      <AddressView resources={this.props.resources} address={this.props.guest.primaryAddress} rootCss={styles.detailColumn}/>

      {/* Email / Phone */}
      <div css={css`
             ${flexColumn};
             justify-content: space-between;
             ${styles.detailColumn};
           `}>
        <span id={`email-${this.props.guest.masterGuestId}`} title={getResource(this.props.resources, 'HORIZONTAL_GUEST_DETAIL_PRIMARY_EMAIL')} css={styles.detailLine}>{
          this.props.guest.primaryEmail || getResource(this.props.resources, 'HORIZONTAL_GUEST_DETAIL_NO_EMAIL')
        }</span>
        <span id={`phone-${this.props.guest.masterGuestId}`} title={getResource(this.props.resources, 'HORIZONTAL_GUEST_DETAIL_PHONE_NUMBER')} css={styles.detailLine}>{
          prettyPhone(this.props.guest.primaryPhone) || getResource(this.props.resources, 'HORIZONTAL_GUEST_DETAIL_NO_PHONE_NUMBER')
        }</span>
      </div>

      {/* Group Menu */}
      <GroupMenu guestId={this.props.guest.masterGuestId}
                 householdSize={this.props.householdSize}
                 disabled={!this.props.isExpanded}
                 rootCss={groupMenu}/>
    </div>
  );
};
